import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject, type RefObject } from "react";
import type { DesktopChatThread } from "../../../shared/desktopChatThreads";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import { Spinner } from "../ui/spinner";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import type { Chapter, ChapterComment, ChapterVariation } from "../../types";
import { ChevronLeftIcon, ChevronRightIcon, IconPencil, IconX } from "./icons";
import { AssistantModeSwitcher } from "./AssistantModeSwitcher";
import {
  COMMENT_FILTER_ALL,
  DESKTOP_CHAPTER_COMMENT_TYPES,
  chapterCommentTypeClassSlug,
  formatChapterCommentTypeLabel,
  getChapterCommentAttribution,
} from "../../../shared/chapterCommentLabels";
import {
  flattenReplyParentId,
  groupCommentThreads,
  isRootComment,
} from "@repo/shared/chapterCommentThreads";
import { averageStoryCraftScore, type DesktopStoryCraftFeedback } from "../../../shared/storyCraftFeedback";
import { SCENE_TOOLS, type SceneToolId } from "../../../shared/sceneTools";
import { LlmModelSelect } from "./LlmModelSelect";
import { ChapterVariationsPanel } from "./ChapterVariationsPanel";
import { cn } from "../../lib/utils";
import { LuArchive } from "react-icons/lu";

export type { SceneToolId };

export type AssistantPanelTab = "comments" | "chat" | "variations" | "notes" | "actions";

export type ChatModelValue = string;

export type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  scope: "chapter" | "book";
  chapterId: string | null;
  createdAt: string;
};

export function LoadingDots() {
  return <Spinner size={14} aria-label="Assistant is thinking" />;
}

const COMMENT_TYPE_BADGE_CLASSES: Record<string, string> = {
  suggestion: "border-blue-500/45 bg-blue-500/20 text-blue-800 dark:text-blue-200",
  issue: "border-red-500/45 bg-red-500/20 text-red-800 dark:text-red-200",
  praise: "border-emerald-500/45 bg-emerald-500/20 text-emerald-800 dark:text-emerald-200",
  question: "border-amber-500/45 bg-amber-500/20 text-amber-800 dark:text-amber-200",
  note: "border-violet-500/45 bg-violet-500/20 text-violet-800 dark:text-violet-200",
  highlight: "border-yellow-600/55 bg-yellow-500/25 text-yellow-800 dark:text-yellow-200",
};

export function AssistantPanel({
  activeChapter,
  assistantPanelTab,
  onAssistantTabChange,
  onCollapse,
  chatBusy,
  activeChats,
  chatThreads,
  archivedChatThreads,
  activeChatThreadId,
  onSelectChatThread,
  onRestoreChatThread,
  onNewChatThread,
  onArchiveChat,
  chatStickToBottomRef,
  contextChapterTitle,
  chatDraft,
  onChatDraftChange,
  onSendChat,
  onStopChat,
  chatInputRef,
  chatModel,
  onChatModelChange,
  chatModelsOpenAi,
  chatModelsAnthropic,
  uploadedChatImage,
  onRemoveUploadedImage,
  chatImageInputRef,
  onChatImagePick,
  onUploadChatImage,
  attachedChatVersion,
  chatVersionOptions,
  onAttachChatVersion,
  onRemoveAttachedChatVersion,
  starterPrompts,
  renderMarkdown,
  onClearChat,
  onAddComment,
  onUpdateComment,
  onToggleCommentResolved,
  onJumpToComment,
  onDeleteComment,
  getCommentStale,
  selectedCommentId,
  canEditComment,
  canResolveComment,
  canDeleteComment,
  onOpenSelectionComment,
  assistantNotes,
  onAssistantNotesChange,
  runStoryCraft,
  generateSummary,
  runEntityExtraction,
  activeStoryCraft,
  storyCraftBusy,
  summaryBusy,
  storyCraftModel,
  onStoryCraftModelChange,
  applyStoryCraftDraft,
  applyStoryCraftNotes,
  onGenerateVariationFromStoryCraftChecklist,
  chapterVariations,
  activeVariationId,
  onOpenVariationCompare,
  onDismissVariation,
  onStartVariationChat,
  onGenerateVariationFromChat,
  onSaveChatToResearch,
  onRequestChapterVariation,
  onStopVariation,
  onSteerVariation,
  variationBusy = false,
  onSceneTool,
}: {
  activeChapter: Chapter;
  assistantPanelTab: AssistantPanelTab;
  onAssistantTabChange: (tab: AssistantPanelTab) => void;
  onCollapse: () => void;
  chatBusy: boolean;
  activeChats: ChatMessage[];
  chatThreads: DesktopChatThread[];
  archivedChatThreads: DesktopChatThread[];
  activeChatThreadId: string | null;
  onSelectChatThread: (threadId: string) => void;
  onRestoreChatThread: (threadId: string) => void;
  onNewChatThread: () => void;
  onArchiveChat: () => void;
  chatStickToBottomRef: MutableRefObject<boolean>;
  contextChapterTitle: string | null;
  chatDraft: string;
  onChatDraftChange: (value: string) => void;
  onSendChat: () => void;
  onStopChat: () => void;
  chatInputRef: RefObject<HTMLTextAreaElement | null>;
  chatModel: ChatModelValue;
  onChatModelChange: (model: ChatModelValue, provider: "openai" | "anthropic") => void;
  chatModelsOpenAi: readonly { value: string; label: string }[];
  chatModelsAnthropic: readonly { value: string; label: string }[];
  uploadedChatImage: { name: string } | null;
  onRemoveUploadedImage: () => void;
  chatImageInputRef: RefObject<HTMLInputElement | null>;
  onChatImagePick: () => void;
  onUploadChatImage: (file: File | null) => void;
  attachedChatVersion: { id: string; name: string; status: string } | null;
  chatVersionOptions: Array<{ id: string; name: string; status: string }>;
  onAttachChatVersion: (versionId: string) => void;
  onRemoveAttachedChatVersion: () => void;
  starterPrompts: Record<string, string>;
  renderMarkdown: (text: string) => string;
  onClearChat: () => void;
  onAddComment: (
    text: string,
    type: string,
    opts?: {
      anchor?: { from: number; to: number; excerpt?: string };
      blockId?: string | null;
      parentCommentId?: string | null;
    }
  ) => void;
  onUpdateComment: (commentId: string, text: string) => void;
  onToggleCommentResolved: (commentId: string) => void;
  onJumpToComment: (commentId: string) => void;
  onDeleteComment: (commentId: string) => void;
  getCommentStale: (comment: ChapterComment) => boolean;
  selectedCommentId?: string | null;
  canEditComment?: (comment: ChapterComment) => boolean;
  canResolveComment?: (comment: ChapterComment) => boolean;
  canDeleteComment?: (comment: ChapterComment) => boolean;
  onOpenSelectionComment?: () => void;
  assistantNotes: string;
  onAssistantNotesChange: (value: string) => void;
  runStoryCraft: () => void;
  generateSummary: () => void;
  runEntityExtraction: () => void;
  activeStoryCraft: DesktopStoryCraftFeedback | null;
  storyCraftBusy: boolean;
  summaryBusy: boolean;
  storyCraftModel: ChatModelValue;
  onStoryCraftModelChange: (model: ChatModelValue, provider: "openai" | "anthropic") => void;
  applyStoryCraftDraft: () => void;
  applyStoryCraftNotes: () => void;
  onGenerateVariationFromStoryCraftChecklist?: () => void;
  chapterVariations: ChapterVariation[];
  activeVariationId: string | null;
  onOpenVariationCompare: (variationId: string) => void;
  onDismissVariation: (variationId: string) => void;
  onStartVariationChat: () => void;
  onGenerateVariationFromChat: () => void;
  onSaveChatToResearch?: () => void;
  onRequestChapterVariation: (instructions?: string) => void;
  onStopVariation: () => void;
  onSteerVariation: (direction: string) => void;
  variationBusy?: boolean;
  onSceneTool?: (toolId: SceneToolId) => void;
}) {
  const hasHistory = activeChats.length > 0;
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const lastThreadIdRef = useRef<string | null>(null);

  const handleChatScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    chatStickToBottomRef.current = distance < 96;
  };

  useLayoutEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    if (activeChatThreadId !== lastThreadIdRef.current) {
      lastThreadIdRef.current = activeChatThreadId;
      el.scrollTop = 0;
      chatStickToBottomRef.current = false;
      return;
    }
    if (chatStickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [activeChats, chatBusy, activeChatThreadId, chatStickToBottomRef]);
  const [commentDraft, setCommentDraft] = useState("");
  const [newCommentType, setNewCommentType] = useState<string>("note");
  const [commentFilterType, setCommentFilterType] = useState<string>(COMMENT_FILTER_ALL);
  const [commentScopeFilter, setCommentScopeFilter] = useState<"active" | "resolved" | "all">("active");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");

  useEffect(() => {
    setCommentDraft("");
    setNewCommentType("note");
    setCommentFilterType(COMMENT_FILTER_ALL);
    setCommentScopeFilter("active");
    setEditingCommentId(null);
    setEditDraft("");
    setReplyToCommentId(null);
    setReplyDraft("");
  }, [activeChapter.id]);

  const unresolvedComments = useMemo(
    () => activeChapter.comments.filter((c) => !c.resolved && isRootComment(c)),
    [activeChapter.comments]
  );

  const openVariationCount = useMemo(
    () => chapterVariations.filter((variation) => variation.status === "open").length,
    [chapterVariations]
  );

  const commentTypeOptions = useMemo(() => {
    const s = new Set<string>();
    for (const c of unresolvedComments) {
      if (c.type) s.add(c.type);
    }
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [unresolvedComments]);

  const scopedComments = useMemo(() => {
    return activeChapter.comments.filter((c) => {
      if (commentScopeFilter === "active") return !c.resolved;
      if (commentScopeFilter === "resolved") return !!c.resolved;
      return true;
    });
  }, [activeChapter.comments, commentScopeFilter]);

  const filteredThreads = useMemo(() => {
    const rootIds = new Set(
      scopedComments
        .filter((c) => {
          if (!isRootComment(c)) return false;
          if (commentFilterType === COMMENT_FILTER_ALL) return true;
          return (c.type ?? "note") === commentFilterType;
        })
        .map((c) => c.id)
    );
    const withRoots = scopedComments.filter((c) => {
      if (isRootComment(c)) return rootIds.has(c.id);
      const rootId = flattenReplyParentId(c.parentCommentId, scopedComments) ?? c.parentCommentId;
      return rootId != null && rootIds.has(rootId);
    });
    return groupCommentThreads(withRoots);
  }, [scopedComments, commentFilterType]);

  useEffect(() => {
    if (!selectedCommentId) return;
    const el = document.querySelector(`[data-chapter-comment-id="${selectedCommentId}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [selectedCommentId, filteredThreads.length, commentScopeFilter]);

  const unresolvedCount = unresolvedComments.length;
  const totalCommentCount = activeChapter.comments.filter((c) => isRootComment(c)).length;

  useEffect(() => {
    if (commentFilterType === COMMENT_FILTER_ALL) return;
    const types = new Set(
      activeChapter.comments
        .filter((c) => !c.resolved)
        .map((c) => c.type)
        .filter((t): t is string => Boolean(t))
    );
    if (!types.has(commentFilterType)) {
      setCommentFilterType(COMMENT_FILTER_ALL);
    }
  }, [commentFilterType, activeChapter.comments]);

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-l border-border bg-(--workbench-sideBar)" role="region" aria-label="Assistant panel">
      <Card className="isolate m-1.5 h-full min-h-0 gap-3 overflow-hidden rounded-lg border border-border bg-card py-0 shadow-sm ring-0">
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/85 bg-muted/10 px-4 pb-[0.45rem] pt-1.5">
          <div className="flex min-w-0 items-center gap-0.5">
            <Button size="icon-sm" variant="ghost" className="h-[1.85rem] w-[1.85rem] min-w-[1.85rem] p-0 text-muted-foreground hover:text-foreground [&_svg]:size-4" onClick={onCollapse} title="Collapse assistant panel" aria-label="Collapse assistant panel">
              <ChevronRightIcon />
            </Button>
            <span className="text-[0.8125rem] font-semibold tracking-[-0.01em] text-foreground">Assistant</span>
          </div>
          {assistantPanelTab === "chat" ? (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                size="sm"
                variant="secondary"
                className="h-7 px-2 text-xs font-medium"
                disabled={chatBusy}
                onClick={onNewChatThread}
              >
                New chat
              </Button>
              {hasHistory ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-xs font-medium"
                  disabled={chatBusy}
                  onClick={onClearChat}
                >
                  Clear
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex h-full min-h-0 flex-1 flex-col gap-[0.45rem] overflow-hidden px-4 pb-[0.85rem] pt-[0.65rem]">
          <AssistantModeSwitcher
            activeId={assistantPanelTab}
            unresolvedCommentCount={unresolvedCount}
            openVariationCount={openVariationCount}
            variationBusy={variationBusy}
            onSelect={onAssistantTabChange}
          />

          {assistantPanelTab === "chat" ? (
            <div className="flex min-h-0 flex-1 flex-col gap-[0.45rem]">
              <div
                className="flex shrink-0 items-center gap-1.5 px-1 pb-2"
                data-slot="assistant-chat-context-controls"
              >
                <Select
                  value={activeChatThreadId ?? undefined}
                  onValueChange={(value) => {
                    if (value.startsWith("archived:")) {
                      onRestoreChatThread(value.slice("archived:".length));
                      return;
                    }
                    onSelectChatThread(value);
                  }}
                  disabled={chatBusy || chatThreads.length === 0}
                >
                  <SelectTrigger
                    id="assistant-chat-thread-select"
                    className="h-8 min-w-0 flex-1 text-[0.78rem]"
                    aria-label="Chat history"
                    title="Chat history"
                  >
                    <SelectValue placeholder="Current chat" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Chats</SelectLabel>
                      {chatThreads.map((thread) => (
                        <SelectItem key={thread.id} value={thread.id}>
                          {thread.title}
                          {thread.messages.length > 0 ? ` (${thread.messages.length})` : ""}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    {archivedChatThreads.length > 0 ? (
                      <SelectGroup>
                        <SelectLabel>Archived · select to restore</SelectLabel>
                        {archivedChatThreads.map((thread) => (
                          <SelectItem key={thread.id} value={`archived:${thread.id}`}>
                            {thread.title}
                            {thread.messages.length > 0 ? ` (${thread.messages.length})` : ""}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ) : null}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  className="size-8 shrink-0"
                  disabled={chatBusy || activeChats.length === 0}
                  onClick={onArchiveChat}
                  aria-label="Archive current chat"
                  title={
                    activeChats.length === 0
                      ? "Blank chats are reused automatically"
                      : "Archive current chat"
                  }
                >
                  <LuArchive aria-hidden />
                </Button>
              </div>
              {contextChapterTitle ? (
                <p className="mx-1 mb-2 text-xs text-muted-foreground">
                  Next message uses context from: <strong>{contextChapterTitle}</strong>
                </p>
              ) : null}
              <div
                ref={chatScrollRef}
                className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto rounded-lg border border-border/80 bg-card px-3.5 py-3.5 text-foreground"
                aria-live="polite"
                onScroll={handleChatScroll}
              >
                {!hasHistory ? (
                  <EmptyState
                    compact
                    className="w-full self-stretch pb-1.5"
                    title="Start a conversation"
                    description="Ask about your book. Chats stay here when you switch chapters."
                    action={
                      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Suggested prompts">
                        <Button type="button" size="xs" variant="outline" className="rounded-full" onClick={() => onChatDraftChange(starterPrompts.review)}>
                          Review
                        </Button>
                        <Button type="button" size="xs" variant="outline" className="rounded-full" onClick={() => onChatDraftChange(starterPrompts.grammar)}>
                          Grammar
                        </Button>
                        <Button type="button" size="xs" variant="outline" className="rounded-full" onClick={() => onChatDraftChange(starterPrompts.dialogue)}>
                          Dialogue
                        </Button>
                        <Button type="button" size="xs" variant="outline" className="rounded-full" onClick={() => onChatDraftChange(starterPrompts.continuity)}>
                          Continuity
                        </Button>
                        <Button
                          type="button"
                          size="xs"
                          variant="secondary"
                          className="rounded-full border border-primary/40"
                          onClick={onStartVariationChat}
                          disabled={chatBusy || variationBusy}
                        >
                          Variation
                        </Button>
                        {SCENE_TOOLS.map((tool) => (
                          <Button
                            key={tool.id}
                            type="button"
                            size="xs"
                            variant="outline"
                            className="rounded-full"
                            title={tool.shortPrompt}
                            disabled={chatBusy || !onSceneTool}
                            onClick={() => onSceneTool?.(tool.id)}
                          >
                            {tool.label}
                          </Button>
                        ))}
                      </div>
                    }
                  />
                ) : null}
                {activeChats.map((message) => (
                  <div
                    key={message.id}
                    className={cn("flex max-w-[94%]", message.role === "user" ? "self-end" : "self-start")}
                  >
                    <div className="max-w-full">
                      <div
                        className={cn(
                          "whitespace-pre-wrap rounded-lg px-3 py-2.5 text-sm leading-[1.48]",
                          "[&_h2]:my-1.5 [&_h2]:font-semibold [&_h3]:my-1.5 [&_h3]:font-semibold [&_h4]:my-1.5 [&_h4]:font-semibold",
                          "[&_li]:my-0.5 [&_ol]:my-1.5 [&_ol]:pl-4.5 [&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:pl-4.5",
                          "[&_code.inline-code]:rounded-lg [&_code.inline-code]:bg-background/60 [&_code.inline-code]:px-1.5 [&_code.inline-code]:py-0.5 [&_code.inline-code]:font-mono [&_code.inline-code]:text-[0.8em]",
                          "[&_pre.code-block]:my-2 [&_pre.code-block]:overflow-x-auto [&_pre.code-block]:rounded-lg [&_pre.code-block]:border [&_pre.code-block]:border-border [&_pre.code-block]:bg-background/70 [&_pre.code-block]:px-2.5 [&_pre.code-block]:py-2",
                          message.role === "user"
                            ? "rounded-br-sm bg-primary text-primary-foreground [&_code.inline-code]:bg-white/20 [&_pre.code-block]:bg-white/20"
                            : "rounded-bl-sm border border-border bg-secondary/80 text-foreground"
                        )}
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
                      />
                    </div>
                  </div>
                ))}
                {chatBusy ? (
                  <div className="flex max-w-[94%] self-start">
                    <div className="max-w-full">
                      <div className="rounded-lg rounded-bl-sm border border-border bg-secondary/80 px-3 py-2.5 text-sm leading-[1.48] text-foreground">
                        <LoadingDots />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="flex shrink-0 flex-col gap-2">
                {hasHistory ? (
                  <div className="flex flex-wrap justify-end gap-1.5 px-1">
                    {onSaveChatToResearch ? (
                      <Button
                        type="button"
                        size="xs"
                        variant="outline"
                        className="rounded-full px-2.5"
                        disabled={chatBusy}
                        onClick={onSaveChatToResearch}
                        title="Save this chat into the book's research library"
                      >
                        Save to research
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="xs"
                      variant="outline"
                      className="rounded-full px-2.5"
                      disabled={chatBusy || variationBusy}
                      onClick={onGenerateVariationFromChat}
                      title="Generate a chapter variation from this conversation"
                    >
                      {variationBusy ? "Generating variation…" : "Generate variation"}
                    </Button>
                  </div>
                ) : null}
                <div
                  className="flex shrink-0 flex-col rounded-2xl border border-input bg-background shadow-sm transition-[border-color,box-shadow] focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/20"
                  data-slot="assistant-chat-composer"
                >
                  {uploadedChatImage || attachedChatVersion ? (
                    <div className="flex flex-wrap gap-1.5 px-3 pt-2.5">
                      {uploadedChatImage ? (
                        <Badge variant="outline" className="h-auto max-w-full gap-1.5 py-1 text-[0.72rem]">
                          <span className="truncate">{uploadedChatImage.name}</span>
                          <Button type="button" size="icon-xs" variant="ghost" className="-my-1 -mr-1 size-5 rounded-full" onClick={onRemoveUploadedImage} aria-label="Remove image">
                            ×
                          </Button>
                        </Badge>
                      ) : null}
                      {attachedChatVersion ? (
                        <Badge variant="outline" className="h-auto max-w-full gap-1.5 border-primary/40 bg-primary/10 py-1 text-[0.72rem]">
                          <span className="truncate">{attachedChatVersion.name || "Untitled"}</span>
                          <Button
                            type="button"
                            size="icon-xs"
                            variant="ghost"
                            className="-my-1 -mr-1 size-5 rounded-full"
                            onClick={onRemoveAttachedChatVersion}
                            aria-label="Remove attached version"
                          >
                            ×
                          </Button>
                        </Badge>
                      ) : null}
                    </div>
                  ) : null}
                  <Textarea
                    ref={chatInputRef}
                    aria-label="Assistant chat composer"
                    placeholder={hasHistory ? "Message Assistant…" : "Ask about your manuscript…"}
                    rows={3}
                    className="max-h-40 min-h-20 resize-none border-0 bg-transparent px-3 py-3 text-[0.92rem] leading-[1.45] shadow-none focus-visible:border-0 focus-visible:ring-0"
                    value={chatDraft}
                    onChange={(event) => onChatDraftChange(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        onSendChat();
                      }
                    }}
                  />
                  <div className="flex w-full min-w-0 items-center gap-1 border-t border-border/60 px-2 py-2">
                    <input ref={chatImageInputRef} type="file" accept="image/*" className="hidden" onChange={(event) => onUploadChatImage(event.target.files?.[0] ?? null)} />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="ghost"
                          aria-label="Add attachment"
                          title="Add attachment"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="min-w-52">
                        <DropdownMenuItem onSelect={onChatImagePick}>Upload image…</DropdownMenuItem>
                        {chatVersionOptions.length > 0 ? (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel>Attach book version</DropdownMenuLabel>
                            <DropdownMenuRadioGroup
                              value={attachedChatVersion?.id ?? "__none__"}
                              onValueChange={(value) => {
                                if (value === "__none__") onRemoveAttachedChatVersion();
                                else onAttachChatVersion(value);
                              }}
                            >
                              <DropdownMenuRadioItem value="__none__">No version</DropdownMenuRadioItem>
                              {chatVersionOptions.map((v) => (
                                <DropdownMenuRadioItem key={v.id} value={v.id}>
                                  {(v.name || "Untitled").trim() || "Untitled"} ({v.status})
                                </DropdownMenuRadioItem>
                              ))}
                            </DropdownMenuRadioGroup>
                          </>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <div className="min-w-0 flex-1" />
                    <LlmModelSelect
                      value={chatModel}
                      onChange={onChatModelChange}
                      openAiModels={chatModelsOpenAi}
                      anthropicModels={chatModelsAnthropic}
                      className="h-7 min-w-0 max-w-40 shrink text-[0.72rem]"
                      title="Chat model"
                      aria-label="Chat model"
                      disabled={chatBusy}
                    />
                    <Button
                      type="button"
                      size="icon"
                      className={cn(
                        "size-8 rounded-full",
                        chatBusy && "bg-foreground text-background hover:bg-foreground/85"
                      )}
                      disabled={!chatBusy && !chatDraft.trim() && !uploadedChatImage}
                      onClick={chatBusy ? onStopChat : onSendChat}
                      aria-label={chatBusy ? "Stop generating response" : "Send chat message"}
                      title={chatBusy ? "Stop generating" : "Send message"}
                    >
                      {chatBusy ? (
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <rect x="7" y="7" width="10" height="10" rx="1.5" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden>
                          <path d="m5 12 7-7 7 7M12 5v14" />
                        </svg>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {assistantPanelTab === "comments" ? (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-0.5 pt-0.5">
              <div className="flex h-full min-h-0 flex-1 flex-col gap-2">
                <div className="flex shrink-0 flex-col gap-[0.45rem] border-b border-border pb-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="m-0 text-[0.82rem] font-semibold leading-tight text-foreground">Comments</h2>
                    <div className="inline-flex shrink-0 items-center gap-1" aria-label="Comment counts">
                      {unresolvedCount > 0 ? (
                        <Badge variant="destructive" title="Unresolved">
                          {unresolvedCount}
                        </Badge>
                      ) : null}
                      <Badge variant="secondary" title="Total in chapter">
                        {totalCommentCount}
                      </Badge>
                    </div>
                  </div>
                  {totalCommentCount > 0 ? (
                    <div className="flex flex-col gap-1.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <label className="w-11 shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground" htmlFor="assistant-comment-scope">
                          Show
                        </label>
                        <Select
                          value={commentScopeFilter}
                          onValueChange={(value) => setCommentScopeFilter(value as "active" | "resolved" | "all")}
                        >
                          <SelectTrigger
                            id="assistant-comment-scope"
                            className="h-7 min-w-0 flex-1 text-xs"
                            size="sm"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                            <SelectItem value="all">All</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {unresolvedCount > 0 ? (
                        <div className="flex min-w-0 items-center gap-2">
                          <label className="w-11 shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground" htmlFor="assistant-comment-type-filter">
                            Type
                          </label>
                          <Select
                            value={commentFilterType}
                            onValueChange={(value) => setCommentFilterType(value)}
                          >
                            <SelectTrigger
                              id="assistant-comment-type-filter"
                              className="h-7 min-w-0 flex-1 text-xs"
                              size="sm"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={COMMENT_FILTER_ALL}>All types</SelectItem>
                              {commentTypeOptions.map((t) => (
                                <SelectItem key={t} value={t}>
                                  {formatChapterCommentTypeLabel(t)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto px-px pb-1 pt-0.5" role="list" aria-label={`Comments for ${activeChapter.title}`}>
                  {totalCommentCount === 0 ? (
                    <EmptyState
                      compact
                      className="m-0"
                      title="No comments yet"
                      description={
                        <>
                          Select text in the manuscript and click <strong>Comment</strong> in the format toolbar—or
                          use the compose box below.
                        </>
                      }
                    />
                  ) : filteredThreads.length === 0 ? (
                    <EmptyState
                      compact
                      className="m-0"
                      title="No matching comments"
                      description={
                        <>
                          Choose <strong>All types</strong> or switch <strong>Show</strong> to see more.
                        </>
                      }
                    />
                  ) : (
                    <ul className="assistant-chapter-comments-list m-0 flex list-none flex-col gap-2.5 p-0">
                      {filteredThreads.map(({ root: comment, replies }) => {
                        const stale = getCommentStale(comment);
                        const attribution = getChapterCommentAttribution(comment);
                        const typeSlug = chapterCommentTypeClassSlug(comment.type);
                        const displayTarget = (comment.anchor?.excerpt || comment.targetText || "").trim();
                        const isResolved = Boolean(comment.resolved);
                        const isEditing = editingCommentId === comment.id;
                        const isSelected = selectedCommentId === comment.id;
                        const canEdit = canEditComment?.(comment) ?? true;
                        const canResolve = canResolveComment?.(comment) ?? true;
                        const canDelete = canDeleteComment?.(comment) ?? true;
                        const canJump =
                          Boolean(comment.text?.trim()) ||
                          Boolean(displayTarget) ||
                          Boolean(comment.anchor?.from != null && comment.anchor?.to != null);
                        const replyOpen = replyToCommentId === comment.id;
                        return (
                          <li
                            key={comment.id}
                            className={cn(
                              "relative border border-border bg-card/95 px-3 py-2.5 text-[0.8rem] transition-[box-shadow,border-color,background-color] hover:border-primary/30 hover:shadow-md",
                              isResolved && "opacity-70",
                              isSelected && "bg-primary/10 ring-2 ring-ring"
                            )}
                            data-chapter-comment-id={comment.id}
                          >
                            <div className="mb-2 flex items-start justify-between gap-2">
                              <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      "h-auto rounded-sm px-2 py-1 text-[0.62rem] font-bold uppercase leading-tight tracking-[0.08em]",
                                      COMMENT_TYPE_BADGE_CLASSES[typeSlug]
                                    )}
                                  >
                                    {formatChapterCommentTypeLabel(comment.type)}
                                  </Badge>
                                  {stale ? (
                                    <Badge variant="outline" className="h-auto border-amber-500/40 px-1.5 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.04em] text-amber-600 dark:text-amber-300" title="Quoted passage not found in current manuscript">
                                      Stale
                                    </Badge>
                                  ) : null}
                                  {isResolved ? <Badge variant="secondary" className="h-auto px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.04em]">Resolved</Badge> : null}
                                </div>
                                <div className="text-[0.68rem] font-medium leading-snug tracking-[0.01em] text-muted-foreground">{attribution}</div>
                              </div>
                              <div className="flex shrink-0 items-center gap-0.5">
                                {isEditing ? null : (
                                  <>
                                    {canEdit || canResolve || canDelete ? (
                                      <>
                                        {canEdit ? (
                                          <Button
                                            type="button"
                                            size="icon-sm"
                                            variant="ghost"
                                            aria-label="Edit comment"
                                            title="Edit"
                                            onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              setEditingCommentId(comment.id);
                                              setEditDraft(comment.text);
                                            }}
                                          >
                                            <IconPencil size={15} />
                                          </Button>
                                        ) : null}
                                        {canResolve ? (
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="ghost"
                                            aria-label={isResolved ? "Reopen comment" : "Resolve comment"}
                                            title={isResolved ? "Reopen" : "Resolve"}
                                            onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              onToggleCommentResolved(comment.id);
                                            }}
                                          >
                                            <span className="text-[0.62rem] font-bold uppercase tracking-[0.04em]">{isResolved ? "Reopen" : "Resolve"}</span>
                                          </Button>
                                        ) : null}
                                        {canDelete ? (
                                          <Button
                                            type="button"
                                            size="icon-sm"
                                            variant="ghost"
                                            className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                            aria-label="Delete comment"
                                            title="Delete"
                                            onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              onDeleteComment(comment.id);
                                              if (editingCommentId === comment.id) {
                                                setEditingCommentId(null);
                                                setEditDraft("");
                                              }
                                            }}
                                          >
                                            <IconX size={15} />
                                          </Button>
                                        ) : null}
                                      </>
                                    ) : null}
                                  </>
                                )}
                              </div>
                            </div>
                            {isEditing ? (
                              <div className="flex flex-col gap-[0.45rem]">
                                <Textarea
                                  aria-label="Edit comment text"
                                  value={editDraft}
                                  onChange={(event) => setEditDraft(event.target.value)}
                                  className="min-h-18 resize-y"
                                />
                                <div className="flex flex-wrap gap-1.5">
                                  <Button
                                    size="sm"
                                    disabled={!editDraft.trim()}
                                    onClick={() => {
                                      onUpdateComment(comment.id, editDraft);
                                      setEditingCommentId(null);
                                      setEditDraft("");
                                    }}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    onClick={() => {
                                      setEditingCommentId(null);
                                      setEditDraft("");
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                className="block h-auto w-full whitespace-normal rounded-lg p-0 text-left font-normal text-inherit hover:bg-transparent active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-45"
                                disabled={!canJump}
                                title={
                                  canJump
                                    ? displayTarget
                                      ? "Jump to quoted passage in manuscript"
                                      : "Jump to manuscript match for this comment"
                                    : undefined
                                }
                                onClick={() => onJumpToComment(comment.id)}
                              >
                                {comment.text ? <p className="m-0 whitespace-pre-wrap text-[0.8rem] leading-[1.45]">{comment.text}</p> : null}
                                {displayTarget ? <p className="mt-2 border-l-3 border-primary/55 bg-muted/30 px-2.5 py-2 text-[0.76rem] leading-[1.45] text-foreground">{displayTarget}</p> : null}
                                <div className="mt-2 text-xs text-muted-foreground">
                                  {new Date(comment.createdAt).toLocaleString()}
                                </div>
                              </Button>
                            )}
                            {replies.length > 0 ? (
                              <ul className="mt-2 flex list-none flex-col gap-2 border-l-2 border-border/80 pl-2.5">
                                {replies.map((reply) => (
                                  <li key={reply.id} className="m-0 pb-0.5 pt-1.5" data-chapter-comment-id={reply.id}>
                                    <div className="text-[0.68rem] font-medium leading-snug tracking-[0.01em] text-muted-foreground">
                                      {getChapterCommentAttribution(reply)}
                                    </div>
                                    <p className="m-0 whitespace-pre-wrap text-[0.8rem] leading-[1.45]">{reply.text}</p>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {(canEditComment?.(reply) ?? true) ? (
                                        <Button
                                          type="button"
                                          size="xs"
                                          variant="ghost"
                                          onClick={() => {
                                            setEditingCommentId(reply.id);
                                            setEditDraft(reply.text);
                                          }}
                                        >
                                          Edit
                                        </Button>
                                      ) : null}
                                      {(canDeleteComment?.(reply) ?? true) ? (
                                        <Button
                                          type="button"
                                          size="xs"
                                          variant="ghost"
                                          className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                          onClick={() => onDeleteComment(reply.id)}
                                        >
                                          Delete
                                        </Button>
                                      ) : null}
                                    </div>
                                    {editingCommentId === reply.id ? (
                                      <div className="flex flex-col gap-[0.45rem]">
                                        <Textarea
                                          aria-label="Edit reply"
                                          value={editDraft}
                                          onChange={(event) => setEditDraft(event.target.value)}
                                          className="min-h-18 resize-y"
                                        />
                                        <div className="flex flex-wrap gap-1.5">
                                          <Button
                                            size="sm"
                                            disabled={!editDraft.trim()}
                                            onClick={() => {
                                              onUpdateComment(reply.id, editDraft);
                                              setEditingCommentId(null);
                                              setEditDraft("");
                                            }}
                                          >
                                            Save
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="secondary"
                                            onClick={() => {
                                              setEditingCommentId(null);
                                              setEditDraft("");
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                            {!isResolved ? (
                              <div className="mt-2">
                                {replyOpen ? (
                                  <>
                                    <Textarea
                                      aria-label="Reply to comment"
                                      placeholder="Write a reply…"
                                      value={replyDraft}
                                      onChange={(event) => setReplyDraft(event.target.value)}
                                      className="min-h-18 resize-y"
                                    />
                                    <div className="flex flex-wrap gap-1.5">
                                      <Button
                                        size="sm"
                                        disabled={!replyDraft.trim()}
                                        onClick={() => {
                                          const text = replyDraft.trim();
                                          if (!text) return;
                                          onAddComment(text, "note", { parentCommentId: comment.id });
                                          setReplyDraft("");
                                          setReplyToCommentId(null);
                                        }}
                                      >
                                        Reply
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => {
                                          setReplyToCommentId(null);
                                          setReplyDraft("");
                                        }}
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </>
                                ) : (
                                  <Button
                                    type="button"
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => {
                                      setReplyToCommentId(comment.id);
                                      setReplyDraft("");
                                    }}
                                  >
                                    Reply
                                  </Button>
                                )}
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div className="mt-auto flex shrink-0 flex-col gap-2 border-t border-border bg-card/95 pt-2">
                  <p className="text-xs text-muted-foreground">
                    Tip: select manuscript text and use <strong>Comment</strong> in the format bar to keep the quote.
                  </p>
                  {onOpenSelectionComment ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="mb-2"
                      onClick={() => onOpenSelectionComment()}
                    >
                      Comment on selection
                    </Button>
                  ) : null}
                  <div className="flex min-w-0 items-center gap-2">
                    <label className="w-11 shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground" htmlFor="assistant-new-comment-type">
                      Type
                    </label>
                    <Select value={newCommentType} onValueChange={(value) => setNewCommentType(value)}>
                      <SelectTrigger
                        id="assistant-new-comment-type"
                        className="h-7 min-w-0 flex-1 text-xs"
                        size="sm"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DESKTOP_CHAPTER_COMMENT_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {formatChapterCommentTypeLabel(t)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea
                    aria-label="New chapter comment"
                    placeholder="Add chapter comment (uses current selection if any)…"
                    value={commentDraft}
                    onChange={(event) => setCommentDraft(event.target.value)}
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!commentDraft.trim()}
                    onClick={() => {
                      const text = commentDraft.trim();
                      if (!text) return;
                      onAddComment(text, newCommentType);
                      setCommentDraft("");
                    }}
                  >
                    Add comment
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          {assistantPanelTab === "variations" ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-0.5 pb-1.5 pt-0.5">
              <ChapterVariationsPanel
                variations={chapterVariations}
                activeVariationId={activeVariationId}
                onCompare={onOpenVariationCompare}
                onDismiss={onDismissVariation}
                onCreateVariation={onRequestChapterVariation}
                onStop={onStopVariation}
                onSteer={onSteerVariation}
                busy={variationBusy}
              />
            </div>
          ) : null}

          {assistantPanelTab === "notes" ? (
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto px-0.5 pb-1.5 pt-0.5">
              <label
                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                htmlFor="assistant-notes-field"
              >
                Scene notes
              </label>
              <Textarea
                id="assistant-notes-field"
                aria-label="Assistant notes"
                className="min-h-55 flex-1 resize-y border-border/80 bg-card"
                placeholder="Revision checklist, beats to hit, open questions…"
                value={assistantNotes}
                onChange={(event) => onAssistantNotesChange(event.target.value)}
              />
            </div>
          ) : null}

          {assistantPanelTab === "actions" ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-0.5 pb-1.5 pt-0.5">
              <div className="mb-2.5 flex flex-col items-start gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">
                  Analysis model
                </span>
                <LlmModelSelect
                  value={storyCraftModel}
                  onChange={onStoryCraftModelChange}
                  openAiModels={chatModelsOpenAi}
                  anthropicModels={chatModelsAnthropic}
                  className="h-8 w-full max-w-64"
                  title="Book analysis model"
                  aria-label="Book analysis model"
                  disabled={storyCraftBusy || summaryBusy}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={runStoryCraft} disabled={storyCraftBusy}>
                  {storyCraftBusy ? "Running Story Craft…" : "Run Story Craft"}
                </Button>
                <Button size="sm" variant="secondary" onClick={generateSummary} disabled={summaryBusy}>
                  {summaryBusy ? "Generating brief…" : "Generate chapter brief"}
                </Button>
                <Button size="sm" variant="secondary" onClick={runEntityExtraction}>
                  Extract characters
                </Button>
              </div>
              {activeStoryCraft ? (
                <div className="grid gap-2.5">
                  <div className="text-xs text-muted-foreground">
                    Generated {new Date(activeStoryCraft.generatedAt).toLocaleString()}
                    {storyCraftBusy ? " · In progress" : ""}
                  </div>
                  {activeStoryCraft.summary === "Analyzing…" ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <LoadingDots />
                      <span>Analyzing chapter…</span>
                    </div>
                  ) : null}
                  {(() => {
                    const avg = averageStoryCraftScore(activeStoryCraft);
                    if (avg == null) return null;
                    const label =
                      avg >= 4.5 ? "Excellent" : avg >= 3.5 ? "Good" : avg >= 2.5 ? "Fair" : avg >= 1.5 ? "Needs work" : "Poor";
                    return (
                      <div className="text-sm">
                        <strong>Average score:</strong> {avg.toFixed(1)} — {label}
                      </div>
                    );
                  })()}
                  <div>
                    <div className="mb-0.5 text-[0.85rem] font-bold">Summary</div>
                    <p className="whitespace-pre-wrap text-sm">{activeStoryCraft.summary}</p>
                  </div>
                  {activeStoryCraft.checklist.length > 0 ? (
                    <div>
                      <div className="mb-0.5 text-[0.85rem] font-bold">Checklist preview</div>
                      <ul className="m-0 list-disc space-y-1 pl-4 text-xs">
                        {activeStoryCraft.checklist.slice(0, 8).map((item) => (
                          <li key={item.id}>
                            <span className="text-muted-foreground">[{item.category}]</span> {item.suggestion}
                          </li>
                        ))}
                      </ul>
                      {activeStoryCraft.checklist.length > 8 ? (
                        <p className="text-xs text-muted-foreground">
                          + {activeStoryCraft.checklist.length - 8} more in the Story Craft tab
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  {activeStoryCraft.promisesMade.length > 0 || activeStoryCraft.promisesKept.length > 0 ? (
                    <div className="text-xs text-muted-foreground">
                      Promises: {activeStoryCraft.promisesMade.length} introduced here ·{" "}
                      {activeStoryCraft.promisesKept.length} addressed from earlier chapters
                    </div>
                  ) : null}
                  <p className="text-xs text-muted-foreground">
                    Full write-up (scores, checklist, promises) is in the editor <strong>Story Craft</strong> tab.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      onClick={applyStoryCraftDraft}
                      disabled={
                        storyCraftBusy ||
                        activeStoryCraft.summary === "Analyzing…" ||
                        activeStoryCraft.summary.startsWith("Analysis failed")
                      }
                    >
                      Apply checklist to manuscript
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={applyStoryCraftNotes}
                      disabled={
                        storyCraftBusy ||
                        activeStoryCraft.summary === "Analyzing…" ||
                        activeStoryCraft.summary.startsWith("Analysis failed")
                      }
                    >
                      Add checklist to revision notes
                    </Button>
                    {onGenerateVariationFromStoryCraftChecklist ? (
                      <Button
                        size="sm"
                        onClick={onGenerateVariationFromStoryCraftChecklist}
                        disabled={
                          storyCraftBusy ||
                          variationBusy ||
                          activeStoryCraft.summary === "Analyzing…" ||
                          activeStoryCraft.summary.startsWith("Analysis failed") ||
                          !activeStoryCraft.checklist.some(
                            (item) => !item.isCompleted && item.suggestion.trim().length > 0
                          )
                        }
                        aria-busy={variationBusy}
                      >
                        {variationBusy ? "Generating variation…" : "Generate variation from checklist"}
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <EmptyState
                  compact
                  title="No analysis yet"
                  description="Run Story Craft for scores, checklist, summary, and narrative promises. Results open in the Story Craft tab."
                />
              )}
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

export function AssistantPanelRail({ onExpand }: { onExpand: () => void }) {
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-l border-border bg-(--workbench-sideBar)" role="region" aria-label="Assistant panel collapsed">
      <Button type="button" variant="ghost" className="h-full min-h-0 w-full flex-col gap-2 rounded-lg border border-border bg-card/70 px-0 py-2.5 text-muted-foreground hover:border-primary/40 hover:bg-primary/10 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2" onClick={onExpand} title="Show assistant panel" aria-label="Show assistant panel">
        <ChevronLeftIcon />
        <span className="rotate-180 [writing-mode:vertical-rl] text-[0.72rem] font-semibold uppercase tracking-[0.08em]">Assistant</span>
      </Button>
    </div>
  );
}
